import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiArrowLeft, FiAlertCircle, FiEdit, FiTrash2, FiPlus, FiX, FiMapPin, FiUsers, FiCalendar } = FiIcons;

const Tournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();
  
  const emptyTournament = {
    name: '',
    location: '',
    date: '',
    participants: '',
    status: 'Προετοιμασία'
  };
  
  const [formData, setFormData] = useState(emptyTournament);
  
  const statusOptions = [
    'Προετοιμασία',
    'Συμμετοχή επιβεβαιωμένη',
    'Ολοκληρώθηκε',
    'Ακυρώθηκε'
  ];
  
  useEffect(() => {
    fetchTournaments();
  }, []);
  
  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tournaments_aox1234')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      setTournaments(data || []);
    } catch (err) {
      console.error('Error fetching tournaments:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    
    try {
      // Prepare data for submission
      const tournamentData = {
        ...formData,
        updated_at: new Date().toISOString()
      };
      
      let result;
      
      if (editingId) {
        // Update existing tournament
        result = await supabase
          .from('tournaments_aox1234')
          .update(tournamentData)
          .eq('id', editingId);
      } else {
        // Insert new tournament
        result = await supabase
          .from('tournaments_aox1234')
          .insert([{ ...tournamentData, created_at: new Date().toISOString() }]);
      }
      
      if (result.error) throw result.error;
      
      setMessage({ 
        type: 'success', 
        text: editingId 
          ? 'Το τουρνουά ενημερώθηκε επιτυχώς!' 
          : 'Το νέο τουρνουά προστέθηκε επιτυχώς!' 
      });
      
      // Reset form and refresh tournaments list
      resetForm();
      fetchTournaments();
    } catch (err) {
      console.error('Error saving tournament:', err);
      setMessage({ 
        type: 'error', 
        text: `Σφάλμα: ${err.message || 'Προέκυψε πρόβλημα κατά την αποθήκευση'}` 
      });
    }
  };
  
  const handleEdit = (tournament) => {
    setFormData(tournament);
    setEditingId(tournament.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτό το τουρνουά;')) return;
    
    try {
      const { error } = await supabase
        .from('tournaments_aox1234')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      setMessage({ type: 'success', text: 'Το τουρνουά διαγράφηκε επιτυχώς!' });
      fetchTournaments();
    } catch (err) {
      console.error('Error deleting tournament:', err);
      setMessage({ 
        type: 'error', 
        text: `Σφάλμα κατά τη διαγραφή: ${err.message}` 
      });
    }
  };
  
  const resetForm = () => {
    setFormData(emptyTournament);
    setEditingId(null);
    setShowForm(false);
  };

  // Get status style
  const getStatusStyle = (status) => {
    switch (status) {
      case 'Συμμετοχή επιβεβαιωμένη':
        return 'bg-green-100 text-green-800';
      case 'Προετοιμασία':
        return 'bg-yellow-100 text-yellow-800';
      case 'Ολοκληρώθηκε':
        return 'bg-blue-100 text-blue-800';
      case 'Ακυρώθηκε':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Τουρνουά</h1>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className={`flex items-center px-4 py-2 ${showForm ? 'bg-gray-600' : 'bg-primary-600'} hover:bg-primary-700 text-white rounded-md`}
            >
              <SafeIcon icon={showForm ? FiX : FiPlus} className="w-5 h-5 mr-2" />
              {showForm ? 'Ακύρωση' : 'Προσθήκη Τουρνουά'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-md flex items-center ${
              message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
            {message.text}
          </motion.div>
        )}

        {/* Form for adding/editing tournaments */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-white rounded-lg shadow-md p-6 mb-8"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {editingId ? 'Επεξεργασία Τουρνουά' : 'Προσθήκη Νέου Τουρνουά'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Όνομα Τουρνουά *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="π.χ. 2ο Παγκρήτιο Τουρνουά Κοριτσιών"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    Τοποθεσία *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiMapPin} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      required
                      value={formData.location}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="π.χ. Χανιά"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                    Ημερομηνία *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiCalendar} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="date"
                      name="date"
                      required
                      value={formData.date}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="π.χ. Μάιος 2025"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="participants" className="block text-sm font-medium text-gray-700 mb-1">
                    Συμμετέχοντες
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiUsers} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="participants"
                      name="participants"
                      value={formData.participants}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="π.χ. 12 ομάδες από όλη την Κρήτη"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Κατάσταση *
                  </label>
                  <select
                    id="status"
                    name="status"
                    required
                    value={formData.status}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Ακύρωση
                </button>
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  {editingId ? 'Ενημέρωση' : 'Αποθήκευση'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Tournaments List */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Λίστα Τουρνουά</h2>
          
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Φόρτωση...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-4 rounded-md text-red-700">
              <p>{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tournaments.map((tournament, index) => (
                <motion.div
                  key={tournament.id}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-lg p-5 border-l-4 border-primary-600 relative group"
                >
                  <div className="absolute top-2 right-2 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => handleEdit(tournament)}
                      className="p-1 bg-blue-100 rounded-full hover:bg-blue-200 text-blue-600"
                      title="Επεξεργασία"
                    >
                      <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(tournament.id)}
                      className="p-1 bg-red-100 rounded-full hover:bg-red-200 text-red-600"
                      title="Διαγραφή"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{tournament.name}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(tournament.status)}`}>
                      {tournament.status}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-gray-600 text-sm">
                    {tournament.location && (
                      <div className="flex items-center">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2 text-primary-600" />
                        <span>{tournament.location}</span>
                      </div>
                    )}
                    
                    {tournament.date && (
                      <div className="flex items-center">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2 text-primary-600" />
                        <span>{tournament.date}</span>
                      </div>
                    )}
                    
                    {tournament.participants && (
                      <div className="flex items-center">
                        <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2 text-primary-600" />
                        <span>{tournament.participants}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          
          {!loading && tournaments.length === 0 && (
            <p className="text-gray-500 text-center py-4">Δεν υπάρχουν καταχωρημένα τουρνουά</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tournaments;